/**
 * Socket Print Service
 * Connects to the ClientPrintAgent via Socket.IO for silent printing.
 * Falls back gracefully if socket.io-client is not available.
 */

let socket = null;
let connectionPromise = null;

const SOCKET_URL = "http://localhost:5000";
const CONNECT_TIMEOUT = 5000;

/**
 * Get or create the socket connection (singleton)
 */
export const getSocketConnection = () => {
  if (socket && socket.connected) return Promise.resolve(socket);
  if (connectionPromise) return connectionPromise;

  connectionPromise = new Promise(async (resolve, reject) => {
    try {
      const { io } = await import("socket.io-client");
      socket = io(SOCKET_URL, {
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 1000,
        timeout: CONNECT_TIMEOUT,
      });

      const timer = setTimeout(() => {
        connectionPromise = null;
        socket.disconnect();
        reject(new Error("Socket connection timeout"));
      }, CONNECT_TIMEOUT);

      socket.on("connect", () => {
        clearTimeout(timer);
        console.log("🔌 Socket print connected:", socket.id);
        connectionPromise = null;
        resolve(socket);
      });

      socket.on("connect_error", (err) => {
        clearTimeout(timer);
        connectionPromise = null;
        console.error("🔌 Socket print connection error:", err.message);
        reject(err);
      });

      socket.on("disconnect", (reason) => {
        console.log("🔌 Socket print disconnected:", reason);
      });
    } catch (err) {
      connectionPromise = null;
      reject(new Error("socket.io-client not available: " + err.message));
    }
  });

  return connectionPromise;
};

/**
 * Check if socket print is available
 */
export const isSocketPrintAvailable = async () => {
  try {
    const sock = await getSocketConnection();
    return sock && sock.connected;
  } catch {
    return false;
  }
};

/**
 * Disconnect socket
 */
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    connectionPromise = null;
  }
};

/**
 * Get printers list via socket
 */
export const getSocketPrinters = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const sock = await getSocketConnection();
      sock.emit("getPrinters", (response) => {
        if (response.success) {
          resolve(response.printers);
        } else {
          reject(new Error(response.error || "Failed to get printers"));
        }
      });
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Print receipt via socket (silent, no dialog)
 * @param {Object} data - Same fields as the /thermalPrint REST endpoint
 * @returns {Promise}
 */
export const socketThermalPrint = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const sock = await getSocketConnection();
      sock.emit("thermalPrint", data, (response) => {
        if (response.success) {
          console.log("🖨️ Socket receipt print success:", response.message);
          resolve(response);
        } else {
          console.error("🖨️ Socket receipt print failed:", response.error);
          reject(new Error(response.error || "Print failed"));
        }
      });
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Print KOT via socket (silent, no dialog)
 * @param {Object} data - Same fields as the /kotThermalPrint REST endpoint
 * @returns {Promise}
 */
export const socketKotThermalPrint = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const sock = await getSocketConnection();
      sock.emit("kotThermalPrint", data, (response) => {
        if (response.success) {
          console.log("🖨️ Socket KOT print success:", response.message);
          resolve(response);
        } else {
          console.error("🖨️ Socket KOT print failed:", response.error);
          reject(new Error(response.error || "KOT print failed"));
        }
      });
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Print Shift Report (Bill Type) via socket ESC/POS (silent, no dialog)
 * @param {Object} data - Shift report data for thermal printing
 * @returns {Promise}
 */
export const socketShiftReportPrint = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const sock = await getSocketConnection();
      // Timeout guard: if the server doesn't have this handler, the callback never fires
      const timeout = setTimeout(() => {
        reject(new Error("Shift report print timed out (server may not support this event)"));
      }, 8000);
      sock.emit("shiftReportPrint", data, (response) => {
        clearTimeout(timeout);
        if (response.success) {
          console.log("🖨️ Socket shift report print success:", response.message);
          resolve(response);
        } else {
          console.error("🖨️ Socket shift report print failed:", response.error);
          reject(new Error(response.error || "Shift report print failed"));
        }
      });
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Clone a DOM element and inline all computed styles onto every element.
 * This makes the HTML completely self-contained - no external CSS needed.
 * Puppeteer (or any renderer) will display it exactly as it appears on screen.
 */
const cloneWithInlineStyles = (sourceEl) => {
  const clone = sourceEl.cloneNode(true);
  
  const sourceEls = sourceEl.querySelectorAll('*');
  const cloneEls = clone.querySelectorAll('*');
  
  // Copy ALL computed styles from source to clone (pixel-perfect)
  const applyAllStyles = (src, tgt) => {
    const computed = window.getComputedStyle(src);
    for (let i = 0; i < computed.length; i++) {
      const prop = computed[i];
      tgt.style.setProperty(prop, computed.getPropertyValue(prop));
    }
  };
  
  // Root element
  applyAllStyles(sourceEl, clone);
  
  // All child elements
  for (let i = 0; i < sourceEls.length; i++) {
    applyAllStyles(sourceEls[i], cloneEls[i]);
  }
  
  // Convert images to data URLs if possible
  const imgs = clone.querySelectorAll('img');
  imgs.forEach((img, idx) => {
    const srcImg = sourceEl.querySelectorAll('img')[idx];
    if (srcImg && srcImg.complete && srcImg.naturalWidth > 0) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = srcImg.naturalWidth;
        canvas.height = srcImg.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(srcImg, 0, 0);
        img.src = canvas.toDataURL('image/png');
      } catch (e) {
        // CORS - skip
      }
    }
  });
  
  return clone.outerHTML;
};

/**
 * Print HTML via socket using Puppeteer on backend (browser-quality rendering)
 * Inlines all computed styles so the template renders exactly as on screen.
 * @param {string} printerName - Name of the printer
 * @param {HTMLElement} receiptElement - The actual DOM element (not outerHTML string)
 * @param {string} paperWidth - Paper width ('58' or '80')
 * @returns {Promise}
 */
export const socketHtmlPrint = (printerName, receiptElement, paperWidth) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Clone element with all computed styles inlined
      const styledHtml = typeof receiptElement === 'string' 
        ? receiptElement 
        : cloneWithInlineStyles(receiptElement);

      const sock = await getSocketConnection();
      sock.emit("htmlPrint", { printerName, html: styledHtml, paperWidth }, (response) => {
        if (response.success) {
          console.log("🖨️ Socket HTML print success:", response.message);
          resolve(response);
        } else {
          console.error("🖨️ Socket HTML print failed:", response.error);
          reject(new Error(response.error || "HTML print failed"));
        }
      });
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Helper: Convert FormData to plain object for socket emission
 * (Since socket.io can't send FormData directly)
 */
export const formDataToObject = (formData) => {
  const obj = {};
  for (const [key, value] of formData.entries()) {
    // Skip file fields (logo) - socket print doesn't support file upload
    if (value instanceof File || value instanceof Blob) continue;
    obj[key] = value;
  }
  return obj;
};
